import _ from 'lodash';

import { store } from '@store';

import { ErrorStateType } from '@models';
import i18n from '@configs/i18n';
import routes from '@configs/routes.json';

import { setError } from '@store/actions';

import { RequestError, HttpStatusCode } from '@utils';

let resolveCsrfToken: (csrfToken: string) => void;
const csrfPromise = new Promise((resolve) => (resolveCsrfToken = resolve));

export const setCsrfToken = (token: string) => {
  resolveCsrfToken(token);
};

export interface IRequestOptions {
  init?: RequestInit;
  skipAuthorizationHandler?: boolean;
}

export const handleResponse = (
  response: Response,
  options?: IRequestOptions
): Promise<Response | RequestError> => {
  if (!response.ok) {
    return response
      .text()
      .then((text) => {
        let code = undefined;
        // Try and parse the code out of the response. If it doesn't exist or the parse throws
        // an error we can just ignore and throw the RequestError with an empty code
        try {
          const json = JSON.parse(text);
          code = json.code;
        } catch (err) {}
        throw new RequestError(response.statusText, response.status, code);
      })
      .catch((error: RequestError) => {
        if (options?.skipAuthorizationHandler) {
          return error;
        }

        // If the response is 401 on a normal request (i.e not log in request) then we can assume
        // the session has expired and change the state to logged out
        if (response.status === HttpStatusCode.UNAUTHORIZED) {
          document.location.pathname = routes.app.login;
        }

        if (response.status === HttpStatusCode.FORBIDDEN) {
          switch (error.code) {
            case ErrorStateType.MissingAccountAssociation:
              store.dispatch(
                setError(ErrorStateType.MissingAccountAssociation)
              );
              break;
            case ErrorStateType.InvalidTOS:
              store.dispatch(setError(ErrorStateType.InvalidTOS));
              break;
          }
        }

        return error;
      });
  }
  return Promise.resolve(response);
};

const headers = () => {
  let isAdaptiveAdmin, accountId, projectId;
  const userExists = store.getState().authentication.user;
  const activeAccountId = store.getState().projectPicker.activeAccountId;
  const activeProjectId = store.getState().projectPicker.activeProjectId;
  if (userExists) {
    isAdaptiveAdmin = userExists.adaptiveSuperUser;
    accountId = isAdaptiveAdmin
      ? null
      : activeAccountId
      ? activeAccountId
      : userExists.accountId;

    const permissions = !isAdaptiveAdmin ? userExists.permissions : null;

    projectId = activeProjectId
      ? activeProjectId
      : permissions && permissions.length > 0
      ? permissions[0].projectId
      : null;
  }

  return _.pickBy({
    'Current-Account-Id': accountId,
    'Current-Project-Id': projectId,
    'Content-Language': i18n.language,
  });
};

export const get = (
  uri: string,
  options?: IRequestOptions
): Promise<Response> => {
  const init = Object.assign(
    {
      method: 'GET',
      credentials: 'include',
      mode: 'same-origin',
      headers: {},
    } as RequestInit,
    options?.init || {}
  );

  Object.assign(init.headers, headers());

  return fetch(uri, init)
    .then((response: Response) => handleResponse(response, options))
    .then((response: Response | RequestError) => {
      if (response instanceof RequestError) {
        throw response;
      }
      return response;
    });
};

export const post = (
  uri: string,
  options?: IRequestOptions
): Promise<Response> => {
  const init = Object.assign(
    {
      method: 'POST',
      credentials: 'include',
      mode: 'same-origin',
      headers: {},
    } as RequestInit,
    options?.init || {}
  );

  Object.assign(init.headers, headers());

  return csrfPromise
    .then((csrfToken) => {
      (init.headers as any)['Csrf-Token'] = csrfToken;
      return fetch(uri, init);
    })
    .then((response: Response) => handleResponse(response, options))
    .then((response: Response | RequestError) => {
      if (response instanceof RequestError) {
        throw response;
      }
      return response;
    });
};
